import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CELL_WIDTH } from '../../utils/gridConstants';
import { formatWeekDatesSeparate, getYearFromWeek } from '../../utils/weekUtils';

const WeekHeaderContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 12;
  width: fit-content;
  min-width: 100%;
  border-radius: 0 18px 0 0;
  overflow: hidden;
  margin-bottom: 1px;
`;

const WeekCell = styled.div`
  width: ${CELL_WIDTH}px;
  min-width: ${CELL_WIDTH}px;
  flex-shrink: 0;
  padding: 8px;
  text-align: center;
  font-size: 16px;
  color: #444444;
  border-right: 1px solid #eeeeee;
  border-top: ${(props) => (props.isCurrent ? '1px solid #EEEEEE' : '0px')};
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #ffffff;

  .week-number {
    font-size: 16px;
    font-weight: 700;
    color: ${(props) => (props.isCurrent ? '#FFFFFF' : '#9E9E9E')};
    line-height: 1.5;

    ${(props) => props.isCurrent
      && `
      background: #444444;
      border-radius: 4px;
      padding: 0 8px;
      display: inline-block;
    `}
  }

  .week-date {
    font-size: 16px;
    font-weight: 400;
    color: #444444;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
`;

const WeekYearBadge = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #444444;
  padding: 2px 4px;
  background: #eeeeee;
  border-radius: 4px;
  letter-spacing: 0.02em;
  line-height: 1.5;
  transition: all 0.3s ease;
  animation: ${(props) => (props.hasChanged ? 'fadeIn 0.4s ease' : 'none')};

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const WeekNumberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
`;

const GridSpacer = styled.div`
  flex-shrink: 0;
  width: ${(props) => props.width || 0}px;
`;

/**
 * WeekHeader component for the planner grid.
 * Displays a sticky header row with week numbers, dates, and year badges.
 *
 * @param {Object} props
 * @param {string[]} props.weeks - Full array of week strings for calculating total width
 * @param {string[]} props.renderedWeeks - Windowed array of weeks to render
 * @param {string} props.currentWeekString - Current week identifier (e.g., "2025-W01")
 * @param {number|null} props.previousYear - Previous year for transition animation
 * @param {number} props.renderOffsetPx - Pixel offset for virtual scrolling spacer
 */
const WeekHeader = memo(({
  weeks,
  renderedWeeks,
  currentWeekString,
  previousYear,
  renderOffsetPx,
}) => {
  const todayYear = new Date().getFullYear();

  return (
    <WeekHeaderContainer
      style={{
        width: `${weeks.length * CELL_WIDTH}px`,
      }}
    >
      {renderOffsetPx > 0 && <GridSpacer width={renderOffsetPx} />}
      {renderedWeeks.map((week) => {
        const isCurrent = week === currentWeekString;
        const weekMatch = week.match(/^(\d{4})-W(\d{2})$/);
        const weekNumber = weekMatch ? parseInt(weekMatch[2], 10) : '';
        const dates = formatWeekDatesSeparate(week);
        const weekYear = getYearFromWeek(week);
        const showYearBadge = weekYear !== todayYear;

        return (
          <WeekCell key={week} isCurrent={isCurrent}>
            <WeekNumberContainer>
              <div style={{ width: '100%' }} />
              {showYearBadge ? (
                <WeekYearBadge hasChanged={previousYear !== null}>
                  {weekYear}
                </WeekYearBadge>
              ) : (
                <div style={{ height: '22px' }} />
              )}
            </WeekNumberContainer>
            <div className='week-number'>
              W
              {weekNumber}
            </div>
            <div className='week-date'>
              {dates.start}
              {' '}
              -
              {dates.end}
            </div>
          </WeekCell>
        );
      })}
    </WeekHeaderContainer>
  );
});

WeekHeader.displayName = 'WeekHeader';

WeekHeader.propTypes = {
  weeks: PropTypes.arrayOf(PropTypes.string).isRequired,
  renderedWeeks: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentWeekString: PropTypes.string.isRequired,
  previousYear: PropTypes.number,
  renderOffsetPx: PropTypes.number,
};

WeekHeader.defaultProps = {
  previousYear: null,
  renderOffsetPx: 0,
};

export default WeekHeader;

// Also export the GridSpacer for use in other components
export { GridSpacer };
